import { useEffect, useState } from "react";
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { Crosshair, Loader2, MapPin, Search, X } from "lucide-react";

const INDIA_CENTER = [22.5, 79];

// Leaflet's default marker images don't resolve under webpack, so the pin is drawn inline.
const PIN = L.divIcon({
  className: "",
  iconSize: [30, 40],
  iconAnchor: [15, 40],
  html: `<svg width="30" height="40" viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 0C6.7 0 0 6.6 0 14.8 0 25.9 15 40 15 40s15-14.1 15-25.2C30 6.6 23.3 0 15 0z" fill="hsl(18 54% 49%)"/>
    <circle cx="15" cy="14.5" r="5.5" fill="white"/></svg>`,
});

function ClickToPlace({ onPick }) {
  useMapEvents({ click: (e) => onPick({ latitude: e.latlng.lat, longitude: e.latlng.lng }) });
  return null;
}

// Re-centre when the pin is set from outside the map (current location, address search).
function FollowPin({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.setView(position, Math.max(map.getZoom(), 15));
  }, [map, position?.[0], position?.[1]]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

// The dialog zooms in as it opens, so Leaflet measures a too-small box; measure again once it settles.
function FitAfterOpen() {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 250);
    return () => clearTimeout(t);
  }, [map]);
  return null;
}

const round = (n) => Math.round(n * 1e6) / 1e6;

/**
 * value: { latitude, longitude } or null. `address` is used for "Find on map", which asks
 * OpenStreetMap's Nominatim service to look it up.
 */
export default function LocationPicker({ value, onChange, address }) {
  const [locating, setLocating] = useState(false);
  const [searching, setSearching] = useState(false);
  const position = value ? [value.latitude, value.longitude] : null;
  const pick = (v) => onChange({ latitude: round(v.latitude), longitude: round(v.longitude) });

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("This browser can't share its location");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        pick(pos.coords);
      },
      () => {
        setLocating(false);
        toast.error("Couldn't get your location. Check the browser's location permission.");
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const findAddress = async () => {
    setSearching(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=in&q=${encodeURIComponent(address)}`);
      const [hit] = await res.json();
      if (!hit) toast.error("Couldn't find that address. Drop the pin on the map instead.");
      else pick({ latitude: Number(hit.lat), longitude: Number(hit.lon) });
    } catch {
      toast.error("Address search is unavailable. Drop the pin on the map instead.");
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="space-y-2">
      <div data-testid="showroom-map" className="relative h-44 rounded-xl overflow-hidden border border-border isolate">
        <MapContainer center={position || INDIA_CENTER} zoom={position ? 15 : 4} scrollWheelZoom={false} className="h-full w-full">
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitAfterOpen />
          <ClickToPlace onPick={pick} />
          <FollowPin position={position} />
          {position && (
            <Marker
              position={position}
              icon={PIN}
              draggable
              eventHandlers={{ dragend: (e) => pick({ latitude: e.target.getLatLng().lat, longitude: e.target.getLatLng().lng }) }}
            />
          )}
        </MapContainer>
        {!position && (
          <div className="pointer-events-none absolute inset-0 z-[400] flex flex-col items-center justify-center bg-card/50">
            <MapPin className="h-7 w-7 text-accent" fill="currentColor" stroke="white" />
            <p className="mt-1 text-sm text-foreground font-medium">Click the map to drop a pin</p>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="showroom-use-location"
          onClick={useCurrentLocation}
          disabled={locating}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-secondary disabled:opacity-60"
        >
          {locating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Crosshair className="h-3.5 w-3.5" />} Use Current Location
        </button>
        <button
          type="button"
          data-testid="showroom-find-address"
          onClick={findAddress}
          disabled={searching || !address?.trim()}
          title={address?.trim() ? undefined : "Enter the address first"}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-secondary disabled:opacity-50"
        >
          {searching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />} Find address on map
        </button>
        {position && (
          <>
            <span data-testid="showroom-coords" className="text-xs text-muted-foreground font-mono-spec">
              {value.latitude.toFixed(5)}, {value.longitude.toFixed(5)}
            </span>
            <button type="button" onClick={() => onChange(null)} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
              <X className="h-3 w-3" /> Remove pin
            </button>
          </>
        )}
      </div>
    </div>
  );
}
